import type { ReactElement } from "react";
import { Flex, Text, Tag } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import {
  nodeCard,
  nodeCardClickable,
  nodeHeaderText,
  nodeSubHeaderContainer,
  nodeSubHeaderDivider,
  nodeLeftContent,
  nodeRightContent,
  fundFamilyTraceText,
} from "./styles_vanilla.css";
import { formatEumUsd } from "./utils";
import type { EntityData } from "./types";

type ClientNodeProps = {
  nodeType: "client";
  clientName: string;
  softwareEumUsd?: number | null;
  billableFaEumUsd?: number | null;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
};

type BillingGroupNodeProps = {
  nodeType: "billingGroup";
  billingGroupName: string;
  billingGroupEum: number;
  entityCount: number;
  isNotAssigned: boolean;
  isExpanded: boolean;
  onToggle: () => void;
};

type EntityNodeProps = {
  nodeType: "entity";
  entity: EntityData;
  isNotAssigned: boolean;
};

type HierarchyNodeCardProps = ClientNodeProps | BillingGroupNodeProps | EntityNodeProps;

const ClientCard = ({
  clientName,
  softwareEumUsd,
  billableFaEumUsd,
  hasChildren,
  isExpanded,
  onToggle,
}: {
  clientName: string;
  softwareEumUsd?: number | null;
  billableFaEumUsd?: number | null;
  hasChildren: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement => (
  <Flex
    flexDirection="row"
    justifyContent="space-between"
    alignItems="center"
    gap={Sizes.MD}
    className={hasChildren ? nodeCardClickable : nodeCard}
    onClick={hasChildren ? onToggle : undefined}
  >
    <Flex flexDirection="column" className={nodeLeftContent}>
      <Text variant="strong-lg" className={nodeHeaderText} title={clientName}>
        {clientName}
      </Text>
      <Text variant="secondary-sm">Client</Text>
      <Text variant="secondary-sm">Software EUM · {formatEumUsd(softwareEumUsd)}</Text>
      <Text variant="secondary-sm">FA EUM · {formatEumUsd(billableFaEumUsd)}</Text>
    </Flex>
    {hasChildren && (
      <Flex alignItems="center" className={nodeRightContent}>
        <Icon type={isExpanded ? "chevron-up" : "chevron-down"} size="medium" />
      </Flex>
    )}
  </Flex>
);

const BillingGroupCard = ({
  billingGroupName,
  billingGroupEum,
  entityCount,
  isNotAssigned,
  isExpanded,
  onToggle,
}: {
  billingGroupName: string;
  billingGroupEum: number;
  entityCount: number;
  isNotAssigned: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement => {
  const name = billingGroupName ?? "Unknown";
  const eumFormatted = formatEumUsd(billingGroupEum);
  const subHeaders = isNotAssigned ? [eumFormatted] : ["Billing group", eumFormatted];
  const hasChildren = entityCount > 0;

  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap={Sizes.MD}
      className={hasChildren ? nodeCardClickable : nodeCard}
      onClick={hasChildren ? onToggle : undefined}
    >
      <Flex flexDirection="column" className={nodeLeftContent}>
        <Flex alignItems="center" gap="sm">
          {isNotAssigned && (
            <Icon type="exclamation-triangle" size="small" color={Colors.Danger600} />
          )}
          <Text
            variant="strong-lg"
            className={nodeHeaderText}
            title={name}
            style={isNotAssigned ? { color: Colors.Danger600 } : undefined}
          >
            {name}
          </Text>
        </Flex>
        <Flex
          flexDirection="row"
          alignItems="end"
          gap={Sizes.XS}
          className={nodeSubHeaderContainer}
        >
          {subHeaders.map((item, index) => (
            <Flex key={item} flexDirection="row" alignItems="center" gap={Sizes.XS}>
              {index > 0 && (
                <Text variant="secondary-sm" className={nodeSubHeaderDivider}>
                  |
                </Text>
              )}
              <Text variant="secondary-sm">{item}</Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
      {hasChildren && (
        <Flex alignItems="center" className={nodeRightContent}>
          <Icon type={isExpanded ? "chevron-up" : "chevron-down"} size="medium" />
        </Flex>
      )}
    </Flex>
  );
};

const buildFundFamilyChain = (entity: EntityData): string | null => {
  if (!entity.fundFamilyTrace || entity.fundFamilyTrace.length === 0) return null;
  return entity.fundFamilyTrace.join(" → ");
};

const EntityCard = ({
  entity,
  isNotAssigned,
}: {
  entity: EntityData;
  isNotAssigned: boolean;
}): ReactElement => {
  const fundFamilyChain = buildFundFamilyChain(entity);

  return (
    <Flex
      flexDirection="row"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={Sizes.MD}
      className={nodeCard}
    >
      <Flex flexDirection="column" className={nodeLeftContent}>
        <Text variant="regular-md" className={nodeHeaderText} title={entity.entityName}>
          {entity.entityName}
        </Text>
        <Text variant="secondary-sm">Entity · {formatEumUsd(entity.eumValue)}</Text>
        {fundFamilyChain && (
          <Text variant="secondary-sm" className={fundFamilyTraceText} title={fundFamilyChain}>
            {fundFamilyChain}
          </Text>
        )}
      </Flex>
      {isNotAssigned && (
        <Flex alignItems="center" className={nodeRightContent}>
          <Tag variant="warning" label="Not assigned" />
        </Flex>
      )}
    </Flex>
  );
};

export const HierarchyNodeCard = (props: HierarchyNodeCardProps): ReactElement => {
  switch (props.nodeType) {
    case "client":
      return (
        <ClientCard
          clientName={props.clientName}
          softwareEumUsd={props.softwareEumUsd}
          billableFaEumUsd={props.billableFaEumUsd}
          hasChildren={props.hasChildren}
          isExpanded={props.isExpanded}
          onToggle={props.onToggle}
        />
      );
    case "billingGroup":
      return (
        <BillingGroupCard
          billingGroupName={props.billingGroupName}
          billingGroupEum={props.billingGroupEum}
          entityCount={props.entityCount}
          isNotAssigned={props.isNotAssigned}
          isExpanded={props.isExpanded}
          onToggle={props.onToggle}
        />
      );
    case "entity":
      return <EntityCard entity={props.entity} isNotAssigned={props.isNotAssigned} />;
  }
};
